/**
 * Slide-over drawers: the cart, the mobile navigation and the mobile filter
 * panel all share this one implementation.
 *
 * Handles the parts that are easy to get wrong: focus trapping, restoring
 * focus to whatever opened the drawer, Escape to close, click-outside, and
 * locking background scroll without the page jumping as the scrollbar goes.
 */

import { $, $$, on, trapFocus } from '@/lib/dom'
import { app, type AppState } from '@/store/app'

type DrawerName = NonNullable<AppState['openDrawer']>

let releaseTrap: (() => void) | null = null
let lastFocused: HTMLElement | null = null

function lockScroll(locked: boolean): void {
  if (locked) {
    // Compensate for the disappearing scrollbar so the layout does not shift.
    const gap = window.innerWidth - document.documentElement.clientWidth
    document.body.style.overflow = 'hidden'
    if (gap > 0) document.body.style.paddingRight = `${gap}px`
  } else {
    document.body.style.overflow = ''
    document.body.style.paddingRight = ''
  }
}

function render(open: DrawerName | null): void {
  const backdrop = $('[data-drawer-backdrop]')

  $$('[data-drawer]').forEach((drawer) => {
    const name = drawer.dataset.drawer as DrawerName
    const isOpen = name === open

    if (isOpen) {
      drawer.hidden = false
      // Force a reflow so the transition runs from the closed position rather
      // than being collapsed into the same frame as `hidden = false`.
      void drawer.offsetWidth
      drawer.classList.add('is-open')
      drawer.setAttribute('aria-hidden', 'false')

      releaseTrap?.()
      releaseTrap = trapFocus(drawer)
      $<HTMLElement>('[data-drawer-close]', drawer)?.focus()
    } else if (!drawer.hidden) {
      drawer.classList.remove('is-open')
      drawer.setAttribute('aria-hidden', 'true')
      const hide = () => {
        drawer.hidden = true
      }
      drawer.addEventListener('transitionend', hide, { once: true })
      setTimeout(hide, 500) // reduced-motion fallback
    }
  })

  if (backdrop) {
    if (open) {
      backdrop.hidden = false
      void backdrop.offsetWidth
      backdrop.classList.add('is-open')
    } else {
      backdrop.classList.remove('is-open')
      const hide = () => {
        backdrop.hidden = true
      }
      backdrop.addEventListener('transitionend', hide, { once: true })
      setTimeout(hide, 500)
    }
  }

  lockScroll(open !== null)

  if (open === null) {
    releaseTrap?.()
    releaseTrap = null
    // Return focus to the trigger — without this, keyboard users are dumped
    // back at the top of the document.
    lastFocused?.focus()
    lastFocused = null
  }
}

export function openDrawer(name: DrawerName): void {
  lastFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null
  app.set({ openDrawer: name })
}

export function closeDrawer(): void {
  app.set({ openDrawer: null })
}

export function initDrawers(): void {
  app.subscribe((state, previous) => {
    if (state.openDrawer !== previous.openDrawer) render(state.openDrawer)
  })

  $$('[data-drawer-open]').forEach((trigger) => {
    on(trigger, 'click', (event) => {
      event.preventDefault()
      openDrawer(trigger.dataset.drawerOpen as DrawerName)
    })
  })

  $$('[data-drawer-close]').forEach((button) => on(button, 'click', closeDrawer))

  const backdrop = $('[data-drawer-backdrop]')
  if (backdrop) on(backdrop, 'click', closeDrawer)

  on(document, 'keydown', (event) => {
    if (event.key === 'Escape' && app.get().openDrawer) {
      closeDrawer()
    }
  })
}
